/**
 * CampaignStatusBadge - Status indicator for campaign states
 *
 * Displays campaign status with semantic color coding:
 * - draft: slate (not yet activated)
 * - active: emerald with pulse (currently running)
 * - paused: amber (temporarily stopped)
 * - completed: blue (finished)
 * - archived: slate (stored for reference)
 */
import { FileEdit, Play, Pause, CheckCircle, Archive } from 'lucide-react';
import { cn } from '@respectly/ui';
import type { PlatformCampaignStatus } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface CampaignStatusBadgeProps {
  status: PlatformCampaignStatus;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// ============================================
// Configuration
// ============================================

const statusConfig: Record<
  PlatformCampaignStatus,
  { label: string; icon: typeof Play; dotColor: string; badgeClass: string; pulse?: boolean }
> = {
  draft: {
    label: 'Draft',
    icon: FileEdit,
    dotColor: 'bg-slate-500',
    badgeClass:
      'bg-slate-500/10 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400 border-slate-500/20',
  },
  active: {
    label: 'Active',
    icon: Play,
    dotColor: 'bg-emerald-500',
    badgeClass:
      'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 border-emerald-500/20',
    pulse: true,
  },
  paused: {
    label: 'Paused',
    icon: Pause,
    dotColor: 'bg-amber-500',
    badgeClass:
      'bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border-amber-500/20',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle,
    dotColor: 'bg-blue-500',
    badgeClass:
      'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 border-blue-500/20',
  },
  archived: {
    label: 'Archived',
    icon: Archive,
    dotColor: 'bg-slate-400',
    badgeClass:
      'bg-slate-400/10 text-slate-500 dark:bg-slate-400/20 dark:text-slate-400 border-slate-400/20',
  },
};

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-[10px]',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
};

const iconSizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

// ============================================
// Component
// ============================================

export function CampaignStatusBadge({
  status,
  showIcon = true,
  size = 'md',
  className,
}: CampaignStatusBadgeProps) {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        sizeClasses[size],
        config.badgeClass,
        className
      )}
    >
      {showIcon && (
        <span className="relative">
          <Icon className={iconSizeClasses[size]} />
          {config.pulse && (
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          )}
        </span>
      )}
      {config.label}
    </span>
  );
}

// ============================================
// Utility Functions
// ============================================

export function getCampaignStatusColor(status: PlatformCampaignStatus): string {
  const colors: Record<PlatformCampaignStatus, string> = {
    draft: '#64748b',
    active: '#10b981',
    paused: '#f59e0b',
    completed: '#3b82f6',
    archived: '#94a3b8',
  };
  return colors[status];
}

export function getCampaignStatusLabel(status: PlatformCampaignStatus): string {
  return statusConfig[status].label;
}
